import React, { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from "uuid";
import circlesWaterMark from 'src/assets/images/threeCircles.svg';
import { InfoIconEvolutive, MatchedIcon } from 'src/components/Icons';
import { useEventBus } from 'src/hooks';
import {
  ON_UPDATE_STEP_TWO_DATA,
} from 'src/hooks/EventBus/eventTypes';
import { calculateArrayHashCode, getDifferenceBetween, isNullOrEmptyArray } from 'src/util/arrays';
import CombinedQuestions from '../components/CombinedQuestions';
import Tooltip from '../components/ToolTip';
import { StepManager } from '../managers';
import StepTwoLeftContent from '../sections/StepTwoLeftContent';
import { CombinationModal } from './CombinationModal';
import { SendCombinationModal } from './SendCombinationModal';
import * as Ui from './styles';
import { getParsedQuestionData } from '../util';
import { useDispatch, useSelector } from 'react-redux';
import { fetchEvolutiveStep2CrossedQuestions } from 'src/stores/actions/evolutiveCross';
import { QUESTION_SENT, SEND_QUESTIONS_WITH_SAME_SURVEYID } from '../util/Constantes';
import useFlashMessage from 'src/hooks/FlashMessage';
import { handleOnExecuteCombinationActions } from '../actionHandler';
import { mapQuestionsToSurveyIdList } from '../parsers/combinedQuestions';

function StepTwo({
  // Relacionado a visibilidade do passo
  visible = false,
  customerId,
  questionId,
  surveyId,
  questionTitle
}) {
  // states
  // Grupo de combinação atualmente ativo.
  const [activeCombinationGroup, setActiveCombinationGroup] = useState(null);
  const [showSendModal, setShowSendModal] = useState(false);
  const [combinedQuestions, setCombinedQuestions] = useState([]);
  const [selectedSurveyItems, setSelectedSurveyItems] = useState([])
  const [internalData, setInternalData] = useState(null);

  const dispatch = useDispatch();
  const {
    payload: { data: resultData },
    error,
  } = useSelector(
    (state) => state.evolutiveCross.configuration.step_two.crossedQuestions
  );

  // hooks
  const eventBus = useEventBus();
  const [showFlashMessage] = useFlashMessage();

  // refs
  const stepTwoLeftContentRef = useRef(null);

  /**
   * Effects: Atualiza e recupera o estado do redux quando os dados são alterados no passo 1.
   */
  useEffect(() => {
    const data = getParsedSurveyItems(selectedSurveyItems)
    if (!isNullOrEmptyArray(data)) {
      fetchEvolutiveStep2CrossedQuestions(customerId, data, dispatch);
    }
  }, [dispatch, customerId, selectedSurveyItems]);

  /**
   * Callback: realiza o parse dos dados vindo da requisição do redux
   */
  const getParsedData = useCallback((requestResult) => {
    if (!isNullOrEmptyArray(requestResult)) {
      const finalData = [];
      requestResult.forEach((resultData) => {
        const { titulo_pesquisa, perguntas_cruzadas, id_pesquisa } = resultData;

        finalData.push(
          ...perguntas_cruzadas.map(({ id, enunciado }) => ({
            id: `${id_pesquisa}_${id}`,
            mainTitle: enunciado,
            tagTitle: titulo_pesquisa,
          }))
        );
      });

      return finalData;
    }
    return [];
  }, []);

   /**
   * Effects: Seta o estado internal para as perguntas selecionadas no step 1.
   */
  useEffect(() => {
    setInternalData(getParsedData(resultData));
  }, [resultData, getParsedData]);


  /**
  * Effects: intercepta as mudanças ocorridas no passo 1 visando identificar
  * se deverá haver alguma mudança nas combinações automáticas do passo 2
 */
  useEffect(() => {
    const updatedData = withRemovedQuestions();

    if(!isNullOrEmptyArray(updatedData)) {
      autoMatch(updatedData)
    }
  }, [internalData])


  /**
   * CallBack: Realize o auto-match das perguntas no step 2.
   * Por padrão neste ponto o id é uma concatenação de idPesquisa_idPergunta
  */
  const autoMatch = useCallback(data => {

    if (!isNullOrEmptyArray(data)) {
      let newCombinationState = [];  
      let match = [];
      let alreadyExistsIds = [];


      data.forEach(question1 => {
        match = [];
        data.forEach(question2 => {
          
          // formato: idPesquisa_idPergunta
          const id1 = question1.id;
          const id2 = question2.id;
          const id1Pergunta = id1.split('_')[1];
          const id2Pergunta = id2.split('_')[1];

          if(id1 === id2) {
            return;
          }

          /**
           * Se id da pergunta for igual e o idPesquisa_idPergunta não existir no array de controle
           * Na primeira iteração interna as duas perguntas serão adicionadas ao array de match, a partir da segunda iteração somente a question2
          */

          if(id1Pergunta === id2Pergunta && !alreadyExistsIds.includes(id2)) { 
            if(!match.length) {
              match.push(question1, question2);
              alreadyExistsIds.push(id1, id2);

            } else {
              match.push(question2);
              alreadyExistsIds.push(id2);
            }
          }
        });

        if(!isNullOrEmptyArray(match)) {
          newCombinationState.push(
            {
              id: uuidv4(),
              title: match[0].mainTitle,
              type: "secondary",
              group: [...match],
            }
          );
        }
      });
      
      setCombinedQuestions([...combinedQuestions, ...newCombinationState]);
    }
  }, [combinedQuestions, internalData]);

  /**
   * Callbacks: Evento de update de grupo/combinação entre perguntas.
   */
  const onUpdateGroupQuestions = useCallback((selectedQuestions) => {
    const newCombinationState = [
      ...combinedQuestions,
      {
        id: uuidv4(),
        title: selectedQuestions[0].mainTitle,
        type: "secondary",
        group: [...selectedQuestions],
      },
    ];
    
    setCombinedQuestions(newCombinationState);
  }, [combinedQuestions, setCombinedQuestions]);

  const onChangeSendModalVisibility = function() {
    setShowSendModal(true);
  };

  /**
   * Callbacks: Seta o grupo de perguntas principais
   */
  const setPrincipalGroupQuestions = useCallback((combinedQuestions, stepOneOutput) => {
    const temp = [...combinedQuestions];

    temp[0] = {
      id: uuidv4(),
      title: questionTitle,
      group: [
        // Pergunta atualmente selecionada no dashboard
        {
          id: `${surveyId}_${questionId}`,
          mainTitle: questionTitle,
        },
        // Demais perguntas selecionadas na listagem de pesquisas
        ...stepOneOutput.reduce(
          (prev, curr) => [...prev, ...curr.selectedQuestions],
          []
        ),
      ],
      type: "principal",
    };

    setCombinedQuestions(temp);
  }, [questionId, questionTitle, surveyId]);

  /**
   * Callbacks: Lida com o tracking entre o step 1 e o step 2.
   */
  const setTrackingChanges = useCallback((prevSurveyItems, nextSurveyItems) => {
    if (!isNullOrEmptyArray(prevSurveyItems)) {
      // Calculamos a diferença entre as perguntas das pesquisas anteriormente selecionadas
      // e as atualmente selecionadas.
      const difference = getDifferenceBetween(prevSurveyItems, nextSurveyItems);

      if (!isNullOrEmptyArray(difference)) {
        setCombinedQuestions((state) => {
          const tempState = [...state];
    
          // Para cada mudança interceptada no passo 1, pegamos o id da pesquisa que foi removida
          difference.forEach(({ id: surveyId }) => {
            // Iteramos todos os grupos de combinações
            [...tempState].forEach((combination) => {
              // Para cada combinação, recuperamos o array que corresponde um grupo de perguntas combinadas.
              const { group } = combination;
              // Fazemos um rest em group porquê não queremos perder sua referência enquanto o iteramos
              [...group].forEach((groupItem) => {
                // Para cada item do grupo de perguntas combinadas, pegamos o id da pesquisa correspondente
                const [groupSurveyId] = groupItem.id.split('_');
                // Se a pergunta do grupo atualmente iterada tiver id de pesquisa correspondente com o id da pesquisa removida
                if (groupSurveyId === surveyId.toString()) {
                  // Removemos essa pergunta do grupo
                  group.splice(group.indexOf(groupItem), 1);
                }
              });
              // Se após as verificações acima, o grupo tiver um comprimento menor do que dois
              if (group.length < 2) {
                // Significa que não é mais uma combinação, portanto devemos remover o grupo como um todo.
                tempState.splice(tempState.indexOf(combination), 1);
              }
            });
          });
          
          return tempState;
        })
      }
    }
  }, [setCombinedQuestions]);

  /**
  * Callbacks: Remove da listagem de perguntas as perguntas que já fazem parte de um grupo de combinação.
  */
  const withRemovedQuestions = useCallback(() => {

    const removableItems = combinedQuestions.reduce(
      (prev, curr) => [...prev, ...curr.group],
      []
    );

    if (isNullOrEmptyArray(internalData)) {
      return [];
    } else if (isNullOrEmptyArray(removableItems)) {
      return internalData;
    }

    return internalData.filter(
      ({ id }) => !removableItems.some(({ id: rid }) => id === rid)
    );

  }, [combinedQuestions, internalData]);

  /**
   * Effects: Dispara uma notificação para controle de estado do botão avançar
   */
  useEffect(() => {
    StepManager.changeNextButtonVisibility(combinedQuestions.length > 1);
  }, [combinedQuestions]);

  /**
   * Effects: Dispara uma notificação para o StepManager com os dados de 
   * perguntas combinadas
   */
  useEffect(() => {
    const restQuestions = withRemovedQuestions();
    let restGroupQuestions;

    if(!isNullOrEmptyArray(restQuestions)) {
  
      restGroupQuestions = restQuestions.map((question) => (
        {
          group: [question],
          id: uuidv4(),
          title: question.mainTitle
        }
      ))
    }

    StepManager.updateStepData(1, !restGroupQuestions ? [...combinedQuestions] : [...combinedQuestions, ...restGroupQuestions])
  }, [combinedQuestions])

  /**
   * Effects: Torna a modal de envio de uma pergunta para um grupo de combinações visível.
   */
  useEffect(() => {
    eventBus.subscribe(ON_UPDATE_STEP_TWO_DATA, (stepOneOutput) => {
      setSelectedSurveyItems(stepOneOutput);
      setPrincipalGroupQuestions(combinedQuestions, stepOneOutput);
      setTrackingChanges(selectedSurveyItems, stepOneOutput);
    });
  }, [selectedSurveyItems, combinedQuestions]);

  /**
   * Callbacks: Seta o grupo ativo que será renderizado na modal de combinação
   */
  const handleOnSelectCombinationGroup = useCallback((groupIndex) => {
    setActiveCombinationGroup(groupIndex);
  }, [setActiveCombinationGroup]);

  /**
   * Callbacks: Renderiza os grupos de perguntas secundárias
   */
  const renderCombinedQuestions = useCallback(() => {
    return combinedQuestions
      // Removemos o grupo de perguntas principais, que está sempre no índice 0.
      .slice(1)
      .map((item, index) => {
        const { group, title } = item;
        const currentIndex = index + 1;
        const groupItem = group[0];

        return (
          <CombinedQuestions
            key={groupItem.id}
            title={groupItem.mainTitle || title}
            combinatedQuestions={group.length}
            onSelect={handleOnSelectCombinationGroup.bind(null, currentIndex)}
          />
        )
      })
  }, [combinedQuestions, handleOnSelectCombinationGroup]);

  /**
   * Callbacks: COMENTAR!
   */
  const renderDefaultCard = useCallback(() => {
    return (
      <Ui.BeforeCombineCard>
        <Ui.WaterMartkWrapper>
          <Ui.WaterMarkTitle>
            <h3>Combine suas perguntas</h3>
            <span />
            <p>Das avaliações escolhidas, selecione e combine perguntas secundárias semelhantes.</p>
          </Ui.WaterMarkTitle>
          <Ui.WaterMark src={circlesWaterMark} />
        </Ui.WaterMartkWrapper>
      </Ui.BeforeCombineCard>
    );
  }, [])

  /**
   * Callbacks: Fecha a modal de combinação
   */
  const onCloseCombinationModal = useCallback(() => {
    setActiveCombinationGroup(null);
  }, [setActiveCombinationGroup]);

  /**
   * Callbacks: Recebe a nova configuração setada na modal de combinação
   */
  const onSaveCombinationModal = useCallback((newCombinationConfig) => {

    const { questions: newCombinedQuestions, title: newTitle } = newCombinationConfig;
    const groupIndex = activeCombinationGroup;

    let temp = [...combinedQuestions];

    /**
     * Verificação que controla quando os valores do grupo de combinações deve ser atualizado
     * ou quando deve ser excluido
     */
    if (
      !isNullOrEmptyArray(newCombinedQuestions) &&
      newCombinedQuestions.length > 1
    ) {
      temp[groupIndex].group = newCombinedQuestions;
      if (newTitle) {
        temp[groupIndex].title = newTitle;
      }
    } else {
      temp.splice(groupIndex, 1);
    }

    setCombinedQuestions(temp);
    onCloseCombinationModal();
  }, [combinedQuestions, activeCombinationGroup, onCloseCombinationModal]);

  /**
   * Callbacks: Renderiza a modal de combinação
   */
  const renderCombinationModal = useCallback(() => {
    if (typeof activeCombinationGroup === 'number') {
      const { title, group } = combinedQuestions[activeCombinationGroup];
      const groupItem = group[0];

      return (
        <CombinationModal
          groupValues={group}
          groupTitle={groupItem.mainTitle || title}
          onSave={onSaveCombinationModal}
          onCancel={onCloseCombinationModal}
          isOpen
          customerId={customerId}
          // caso seja o grupo principal de perguntas 
          // é removido o botão que realiza remoção
          withRemovableItems={activeCombinationGroup !== 0}
        />
      )
    }
    return null;
  }, [combinedQuestions, activeCombinationGroup, onCloseCombinationModal, onSaveCombinationModal]);

  /**
   * Callbacks: Notifica para o componente mais externo (EvolutiveView) que a listagem de grupo de perguntas
   * combinadas teve uma alteração
   */
  const handleOnMatchQuestions = useCallback((selectedItem, sentQuestionList) => {
    const newState = [...combinedQuestions];
    const groupIndex = newState.findIndex((group) => group.id === selectedItem.id);

    // Success case: Disparado quando o envio das perguntas para um grupo for feito com sucesso.
    const onSuccess = () => {
      const newGroup = {
        ...newState[groupIndex],
        group: [...selectedItem.group, ...sentQuestionList],
      };

      newState[groupIndex] = newGroup;
      setCombinedQuestions(newState);

      // As perguntas selecionadas devem ser resetadas, tanto no contexto do EvolutiveView quanto no StepTwoLeftContent
      stepTwoLeftContentRef.current && stepTwoLeftContentRef.current.reset();

      // Fecha a modal
      handleOnCloseSendModal();

      // Dispara a flash message indicando sucesso na operação
      showFlashMessage(
        QUESTION_SENT.title,
        QUESTION_SENT.message,
        "success"
      );
    };
    
    // Error case
    const onError = () => {
      const { title, message } = SEND_QUESTIONS_WITH_SAME_SURVEYID;
      showFlashMessage(title, message, 'warning');
    };

    // Parseia a lista de perguntas selecionadas para uma lista com seus respectivos id's de pesquisas.
    const sentQuestionsIdList = mapQuestionsToSurveyIdList(sentQuestionList);
    const currentGroupIdList = mapQuestionsToSurveyIdList(newState[groupIndex].group);

    // Lida com o caso de sucesso ou de erro do envio de perguntas para um grupo pré-existente.
    handleOnExecuteCombinationActions(
      sentQuestionsIdList,
      currentGroupIdList,
      onSuccess,
      onError
    );
  }, [combinedQuestions, stepTwoLeftContentRef]);

  function handleOnCloseSendModal() {
    setShowSendModal(false);
  }

  /**
   * Callbacks: Renderiza a modal de envio para um grupo de perguntas combinadas.
   */
  const renderSendCombinedModal = useCallback(() => {
    if (showSendModal) {
      const secondaryQuestions = combinedQuestions && combinedQuestions.filter(question => question.type !== "principal");

      return (
        <SendCombinationModal
          groupValues={secondaryQuestions}
          questionsSent={stepTwoLeftContentRef.current.selectedItems}
          onSave={handleOnMatchQuestions}
          onCancel={handleOnCloseSendModal}
          isOpen
        />
      )
    }

    return null;
  }, [combinedQuestions, stepTwoLeftContentRef, showSendModal, handleOnMatchQuestions])

  /**
   * Callbacks: Retorna o objeto referente ao agrupamento de perguntas principais
   */
  const getPrincipalGroupQuestions = useCallback(() => {
    return combinedQuestions.find(({ type }) => type === 'principal');
  }, [combinedQuestions]);

  /**
   * Callbacks: Converte os dados de perguntas selecionados no passo 1 para o payload final esperado
   * na listagem de perguntas avulsas
   */
  const getParsedSurveyItems = useCallback((data) => {
    const rawData = [
      ...data,
      {
        id: surveyId,
        selectedQuestions: [{ id: `${surveyId}_${questionId}` }],
      },
    ].map(({ id: surveyId, selectedQuestions }) => {
      const [questionId] = selectedQuestions.map(({ id }) =>
        parseInt(id.split("_").pop())
      );
      return {
        surveyId,
        questionId,
      };
    });

    return getParsedQuestionData(rawData);
  }, [surveyId, questionId]);

  const principalGroupQuestion = getPrincipalGroupQuestions();

  return (
    <Ui.Container visible={visible}>
      <Tooltip toolTipTitle={'Na listagem a seguir, selecione e combine as perguntas secundárias semelhantes'} />
      <Ui.Content>
        <StepTwoLeftContent
          ref={stepTwoLeftContentRef}
          internalData={withRemovedQuestions()}
          customerId={customerId}
          onUpdateGroupQuestions={onUpdateGroupQuestions}
          onChangeSendModalVisibility={onChangeSendModalVisibility}
        />
        <Ui.ContentRight>
          <span className="titleWrapper">
            <MatchedIcon />
            <h2 className="rightTitle">Perguntas combinadas - Grupos de perguntas combinadas</h2>
          </span>

          <span className="titleWrapper">
            <InfoIconEvolutive />
            <h3>Grupo de Perguntas principais</h3>
          </span>

          {
            principalGroupQuestion && (
              <CombinedQuestions
                title={principalGroupQuestion.title}
                combinatedQuestions={principalGroupQuestion.group.length}
                // O grupo de perguntas principais sempre está no índice 0.
                onSelect={() => handleOnSelectCombinationGroup(0)}
              />
            )
          }

          <span className="titleWrapper">
            <InfoIconEvolutive />
            <h3>Grupo de Perguntas secundárias</h3>
          </span>
          {combinedQuestions.length > 1
            ? renderCombinedQuestions()
            : renderDefaultCard()
          }
        </Ui.ContentRight>
      </Ui.Content>
      {renderCombinationModal()}
      {renderSendCombinedModal()}
    </Ui.Container>
  );
}

export default React.memo(StepTwo);